import type { UserActivityDataPoint } from '@/lib/api/user-activity';
import type { ActivityInterval } from '@/lib/api/user-activity';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { useIsMobile } from '@/hooks/use-mobile';

interface ActivityChartProps {
  data: UserActivityDataPoint[];
  interval: ActivityInterval;
}

function formatLabel(isoDate: string, interval: ActivityInterval): string {
  const d = new Date(isoDate);
  if (interval === 'day') {
    return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  }
  if (interval === 'week') {
    return `W${getWeekNumber(d)}`;
  }
  return d.toLocaleDateString(undefined, { month: 'short', year: '2-digit' });
}

function getWeekNumber(d: Date): number {
  const start = new Date(d.getFullYear(), 0, 1);
  const diff = d.getTime() - start.getTime();
  return Math.ceil((diff / 86400000 + start.getDay() + 1) / 7);
}

// Categories shown as stacked bars. totalElapsedMinutes is excluded
// because it uses a different unit (minutes vs counts) and would visually
// dominate the chart.
const categories = [
  { key: 'epicRequestsCreated' as const, label: 'Epic Requests', shortLabel: 'ER', color: 'bg-cyan-500' },
  { key: 'epicsCreated' as const, label: 'Epics', shortLabel: 'Epic', color: 'bg-indigo-500' },
  { key: 'featuresCreated' as const, label: 'Features', shortLabel: 'Feat', color: 'bg-blue-500' },
  { key: 'tasksCompleted' as const, label: 'Tasks', shortLabel: 'Tasks', color: 'bg-emerald-500' },
  { key: 'decisionsLogged' as const, label: 'Decisions', shortLabel: 'Dec', color: 'bg-amber-500' },
  { key: 'aiSessions' as const, label: 'AI Sessions', shortLabel: 'Sess', color: 'bg-purple-500' },
];

export function ActivityChart({ data, interval }: ActivityChartProps) {
  const isMobile = useIsMobile();
  const sorted = [...data].reverse();
  // On mobile, show only the most recent 7 data points to avoid overly compressed bars
  const displayed = isMobile ? sorted.slice(-7) : sorted;
  const maxTotal = Math.max(1, ...displayed.map((d) => d.totalActivity));

  const chartHeight = isMobile ? 140 : 224;

  return (
    <div>
      {/* Legend */}
      <div className="flex flex-wrap gap-2 sm:gap-4 mb-3 sm:mb-4 text-xs">
        {categories.map((cat) => (
          <div key={cat.key} className="flex items-center gap-1 sm:gap-1.5">
            <span className={`w-1.5 h-1.5 sm:w-2 sm:h-2 rounded-full ${cat.color}`} />
            <span className="text-muted-foreground">{isMobile ? cat.shortLabel : cat.label}</span>
          </div>
        ))}
      </div>

      {/* Chart */}
      <div className="flex items-end gap-0.5 sm:gap-1" style={{ height: `${chartHeight + 28}px` }}>
        {displayed.map((point, idx) => (
          <TooltipProvider key={idx}>
            <Tooltip>
              <TooltipTrigger asChild>
                <div className="flex-1 flex flex-col items-center gap-0.5 sm:gap-1">
                  <div className="w-full flex flex-col-reverse" style={{ height: `${chartHeight}px` }}>
                    {categories.map((cat) => {
                      const val = point[cat.key];
                      if (val === 0) return null;
                      const segPct = (val / maxTotal) * 100;
                      return (
                        <div
                          key={cat.key}
                          className={`w-full ${cat.color} first:rounded-b last:rounded-t transition-all`}
                          style={{ height: `${segPct}%` }}
                        />
                      );
                    })}
                    {point.totalActivity === 0 && (
                      <div className="w-full bg-muted/40 rounded" style={{ height: '2px' }} />
                    )}
                  </div>
                  <span className="text-[10px] sm:text-xs text-muted-foreground truncate w-full text-center">
                    {formatLabel(point.intervalStart, interval)}
                  </span>
                </div>
              </TooltipTrigger>
              <TooltipContent>
                <div className="text-xs space-y-0.5">
                  <p className="font-medium">{formatLabel(point.intervalStart, interval)}</p>
                  {categories.map((cat) => (
                    <p key={cat.key}>{cat.label}: {point[cat.key]}</p>
                  ))}
                  <p className="font-medium border-t border-border pt-0.5 mt-1">Total: {point.totalActivity}</p>
                </div>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        ))}
      </div>

      {sorted.length === 0 && (
        <div className="h-40 sm:h-64 flex items-center justify-center text-muted-foreground text-sm">
          No activity data
        </div>
      )}
    </div>
  );
}
